import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, AlertTriangle } from 'lucide-react';
import { truncateText, getCategoryColor, resolveImageUrl } from '../../lib/utils';
import { Badge } from '../ui/badge';

// Format date with time
const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-PT', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const NewsCard = ({ news, variant = 'default' }) => {
  const { title, subtitle, category, image_url, created_at, slug, is_breaking, is_featured } = news;

  if (variant === 'featured') {
    return (
      <Link
        to={`/noticia/${slug}`}
        className="group relative block bg-white border border-[#D6D2C4] rounded-sm overflow-hidden card-hover"
        data-testid={`news-card-${slug}`}
      >
        <div className="aspect-[16/9] relative overflow-hidden">
          <img
            src={resolveImageUrl(image_url) || 'https://images.unsplash.com/photo-1555881400-74d7acaacd8b?w=1200&q=80'}
            alt={title}
            loading="eager"
            decoding="async"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />

          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
            <div className="flex items-center gap-2 mb-4">
              <Badge className={`${getCategoryColor(category)} text-white font-['Montserrat'] text-xs uppercase tracking-wider`}>
                {category}
              </Badge>
              {is_breaking && (
                <Badge className="bg-[#E53935] text-white font-['Montserrat'] text-xs uppercase tracking-wider pulse-breaking">
                  <AlertTriangle size={12} className="mr-1" />
                  Urgente
                </Badge>
              )}
              {is_featured && !is_breaking && (
                <Badge className="bg-[#DFAF3A] text-[#1A1A1A] font-['Montserrat'] text-xs uppercase tracking-wider">
                  Destaque
                </Badge>
              )}
            </div>
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-3 leading-tight tracking-tight">
              {title}
            </h2>
            {subtitle && (
              <p className="text-white/90 font-['Inter'] text-sm md:text-base lg:text-lg mb-4 line-clamp-2 leading-relaxed">
                {subtitle}
              </p>
            )}
            <div className="flex items-center text-white/70 text-xs font-['Inter']">
              <span className="flex items-center gap-1.5">
                <Clock size={12} />
                {formatDateTime(created_at)}
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/noticia/${slug}`}
        className="group flex gap-4 bg-white border border-[#D6D2C4] rounded-sm overflow-hidden card-hover p-3"
        data-testid={`news-card-${slug}`}
      >
        <div className="w-28 h-28 md:w-32 md:h-32 flex-shrink-0 rounded-sm overflow-hidden">
          <img
            src={resolveImageUrl(image_url) || 'https://images.unsplash.com/photo-1555881400-74d7acaacd8b?w=400&q=80'}
            alt={title}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex flex-col justify-center flex-1 min-w-0">
          <Badge className={`${getCategoryColor(category)} text-white font-['Montserrat'] text-[10px] uppercase tracking-wider w-fit mb-2`}>
            {category}
          </Badge>
          <h3 className="font-['Playfair_Display'] text-base md:text-lg font-semibold text-[#1A1A1A] mb-2 line-clamp-2 leading-snug group-hover:text-[#0F5A34] transition-colors">
            {title}
          </h3>
          <span className="text-[#5C5C5C] text-xs font-['Inter'] flex items-center gap-1.5">
            <Clock size={10} />
            {formatDateTime(created_at)}
          </span>
        </div>
      </Link>
    );
  }

  // Default card
  return (
    <Link
      to={`/noticia/${slug}`}
      className="group block bg-white border border-[#D6D2C4] rounded-sm overflow-hidden card-hover"
      data-testid={`news-card-${slug}`}
    >
      <div className="aspect-[16/9] relative overflow-hidden">
        <img
          src={resolveImageUrl(image_url) || 'https://images.unsplash.com/photo-1555881400-74d7acaacd8b?w=800&q=80'}
          alt={title}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        {is_breaking && (
          <Badge className="absolute top-3 left-3 bg-[#E53935] text-white font-['Montserrat'] text-[10px] uppercase tracking-wider pulse-breaking">
            <AlertTriangle size={10} className="mr-1" />
            Urgente
          </Badge>
        )}
      </div>
      <div className="p-5">
        <Badge className={`${getCategoryColor(category)} text-white font-['Montserrat'] text-[10px] uppercase tracking-wider mb-3`}>
          {category}
        </Badge>
        <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#1A1A1A] mb-2 line-clamp-2 leading-snug group-hover:text-[#0F5A34] transition-colors">
          {title}
        </h3>
        {subtitle && (
          <p className="text-[#5C5C5C] font-['Inter'] text-sm mb-4 line-clamp-2 leading-relaxed">
            {truncateText(subtitle, 110)}
          </p>
        )}
        <div className="flex items-center text-[#5C5C5C] text-xs font-['Inter']">
          <span className="flex items-center gap-1.5">
            <Clock size={12} />
            {formatDateTime(created_at)}
          </span>
        </div>
      </div>
    </Link>
  );
};

export default NewsCard;
